import { Injectable, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import { Project } from '../dtos/project';
import { Group } from '../dtos/group';
import { UserInfo } from '../dtos/user-info';
import { ProjectService } from './project.service';
import { GroupService } from './group.service';
import { ProfileService } from './profile.service';

@Injectable({ providedIn: 'root' })
export class ProjectContextService {
  readonly projectId = signal<string>('');
  readonly project = signal<Project | null>(null);
  readonly group = signal<Group | null>(null);
  readonly userInfo = signal<UserInfo | null>(null);
  readonly showExtractionQuestions = signal(false);
  readonly guideAnchorRequest = signal<string | null>(null);
  readonly auditTrailPaperId = signal<number | null>(null);
  private loadProjectSub?: Subscription;

  constructor(
    private projectService: ProjectService,
    private groupService: GroupService,
    private profileService: ProfileService,
  ) {}

  loadProject(projectId: string): void {
    if (!projectId) return;
    this.projectId.set(projectId);

    // Cancel any prior in-flight request to prevent stale responses
    this.loadProjectSub?.unsubscribe();

    this.loadProjectSub = this.projectService.getProjectById(projectId).subscribe({
      next: (proj) => {
        if (!proj) return;
        this.project.set(proj);
        this.groupService.getGroupById(proj.groupId).subscribe({
          next: (grp) => this.group.set(grp),
          error: () => this.group.set(null),
        });
      },
      error: () => this.project.set(null),
    });
  }

  loadUserInfo(): void {
    this.profileService.getCurrentUser().subscribe({
      next: (user) => this.userInfo.set(user),
      error: () => this.userInfo.set(null),
    });
  }

  isProjectPI(): boolean {
    const proj = this.project();
    const user = this.userInfo();
    if (!proj || !user) return false;
    return String(proj.piUserId) === String(user.id);
  }

  reset(): void {
    this.projectId.set('');
    this.project.set(null);
    this.group.set(null);
    this.userInfo.set(null);
  }

  openPaperAuditTrail(paperToProjectId: number): void {
    this.auditTrailPaperId.set(paperToProjectId);
  }

  openExtractionQuestions(): void {
    this.showExtractionQuestions.set(true);
  }

  closeExtractionQuestions(): void {
    this.showExtractionQuestions.set(false);
  }

  requestGuide(anchor: string): void {
    this.guideAnchorRequest.set(anchor);
  }
}
